import { wait } from "@/utils/wait";
import {
  BinanceWsPayload,
  BinanceWsStream,
  binanceWsPayloadSchema,
  parseWsResponse,
} from "./schema";

interface BinanceWebSocketMessage {
  method: string;
  params: string[];
  id: number;
}

const BINANCE_STREAM_URL = "wss://stream.binance.com:9443/stream";

 
type WebSocketMessageCallback = (data: any) => void;

let webSocket: WebSocket | null = null;

export const createBinanceWebSocketService = () => {
  const messageCallbacks = new Map<string, WebSocketMessageCallback[]>();

  const openConnection = () => {
    webSocket = new WebSocket(BINANCE_STREAM_URL);

    webSocket.onopen = () => {
      console.log("✅ WebSocket connection opened");
    };

    webSocket.onmessage = ({ data }: MessageEvent) => {
      const parseResult = binanceWsPayloadSchema.safeParse(JSON.parse(data));
      const message = parseResult.success ? parseResult.data : null;
      handleMessage(message);
    };

    webSocket.onerror = (error: Event) => {
      console.warn("❌ WebSocket error:", error);
    };

    webSocket.onclose = () => {
      console.log("🔴 WebSocket connection closed");
    };
  };

  const handleMessage = (
    message: BinanceWsPayload | BinanceWsPayload[] | null | "ping frame",
  ) => {
    if (!message) return;
    if (message === "ping frame") {
      webSocket?.send("pong frame");
      return;
    }

    const messages = Array.isArray(message) ? message : [message];

    for (const item of messages) {
      const streamName = item.stream;

      for (const callback of messageCallbacks.get(streamName) || []) {
        if (!callback) continue;

        const response = parseWsResponse(item);
        callback(response);
      }
    }
  };

  const subscribeToWebSocketStream = (
    streamName: BinanceWsStream | BinanceWsStream[],
    callback: WebSocketMessageCallback,
  ) => {
    const streams = Array.isArray(streamName) ? streamName : [streamName];
    for (const stream of streams) {
      const callbacks = messageCallbacks.get(stream) || [];
      callbacks.push(callback);
      messageCallbacks.set(stream, callbacks);
    }
    sendWebSocketMessage({ method: "SUBSCRIBE", params: streams, id: 1 });
  };

  const unsubscribeFromWebSocketStream = (
    streamName: BinanceWsStream | BinanceWsStream[],
  ) => {
    const streams = Array.isArray(streamName) ? streamName : [streamName];
    for (const stream of streams) {
      const callbacks = messageCallbacks.get(stream) || [];
      callbacks.pop();
      messageCallbacks.set(stream, callbacks);
    }
    sendWebSocketMessage({
      method: "UNSUBSCRIBE",
      params: streams,
      id: 1,
    });
  };

  const sendWebSocketMessage = async (message: BinanceWebSocketMessage) => {
    if (!webSocket) return;
    do {
      if (webSocket?.readyState !== WebSocket.OPEN) {
        await wait(100);
      }
    } while (webSocket?.readyState !== WebSocket.OPEN);
    if (webSocket && webSocket.readyState === WebSocket.OPEN) {
      webSocket.send(JSON.stringify(message));
    } else {
      console.error("WebSocket connection is not open");
    }
  };

  const closeConnection = () => {
    if (!webSocket) return;

    webSocket.close();
    webSocket = null;
  };

  return {
    webSocket,
    openConnection,
    subscribeToWebSocketStream,
    unsubscribeFromWebSocketStream,
    closeConnection,

    streams: messageCallbacks,
  };
};

export const BinanceWsService = createBinanceWebSocketService();
